import { useState, useRef } from 'react';
import { Sparkles, Loader2, X } from 'lucide-react';

/**
 * "Refine" button + collapsible input that appears under every Nano Banana
 * image. Click → inline input opens → type "make the green darker" → submit
 * → backend re-runs Nano Banana with the previous image as a reference + the
 * new prompt. Keeps the design-iteration loop tight without making the user
 * re-attach the image manually.
 *
 * Extracted from pages/AIHub.jsx.
 */
export function RefineImageControl({ imageUrl, imageFilename, onRefine, disabled, attachmentIndex }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const inputRef = useRef(null);

  const handleOpen = () => {
    setOpen(true);
    // Focus the input on next tick so the autoFocus + transition play nicely
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const submit = async () => {
    const t = text.trim();
    if (!t || disabled) return;
    await onRefine({ imageUrl, imageFilename, refinement: t });
    setText('');
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-zinc-200 dark:border-white/10 bg-white/50 dark:bg-zinc-900/40 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-xs font-medium text-zinc-700 dark:text-zinc-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={`ai-hub-refine-image-btn-${attachmentIndex}`}
        title="Refine this image with a follow-up prompt"
      >
        <Sparkles className="w-3.5 h-3.5 text-violet-500" />
        Refine
      </button>
    );
  }

  return (
    <div
      className="flex items-center gap-1.5 w-full max-w-[420px]"
      data-testid={`ai-hub-refine-image-input-${attachmentIndex}`}
    >
      <input
        ref={inputRef}
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
          if (e.key === 'Escape') { setOpen(false); setText(''); }
        }}
        placeholder="Make the green darker · add a coffee bean · remove the text…"
        className="flex-1 px-3 py-1.5 rounded-lg border border-violet-200 dark:border-violet-800/50 bg-white dark:bg-zinc-900 text-xs text-zinc-800 dark:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-400/40"
        disabled={disabled}
      />
      <button
        type="button"
        onClick={submit}
        disabled={disabled || !text.trim()}
        className="px-2.5 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={`ai-hub-refine-image-submit-${attachmentIndex}`}
      >
        {disabled ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Apply'}
      </button>
      <button
        type="button"
        onClick={() => { setOpen(false); setText(''); }}
        className="p-1 rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"
        data-testid={`ai-hub-refine-image-cancel-${attachmentIndex}`}
        aria-label="Cancel refinement"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default RefineImageControl;
